import type { Ref } from 'vue';
import { unref } from 'vue';
import { play, queue } from './audio';
import useAudio from '../stores/useAudio';
import useAudioNarration from './useAudioNarration';
import type { ScreenSchema } from './screen-schemas/types';
import { isIteratedSlot } from './screen-schemas/types';
import { schemaForTemplate } from './screen-schemas/index';
import screenSplitSchema from './screen-schemas/screen-split.schema';
import {
  createScreenAudioTextResolver,
  readPlaceholderValues,
  type NarratableScreen
} from './screenAudioText';

/**
 * Composable that gives a screen template access to its schema-driven audio slots.
 *
 * When `schema` is null (template not yet migrated), all methods are no-ops so
 * it is always safe to call at component setup level.
 *
 * Resolution order for resolveSlot() (matches `docs/dev/jit-audio-schema.md`
 * three-tier model; numbers here are precedence, not tier IDs):
 *   1. Tier 1 — Prop-level audio on the intro slot (`screen.audio` / `screen.speech`); always wins.
 *   2. Tier 3 — JiT runtime synthesis when `audio-ai-jit` is enabled (`resolveNarrationAudio`).
 *   3. Tier 2 — Course S3 pre-recorded audio via `getAudioURL(key)` when JiT is **disabled**
 *      (`key` matches `<audio-marker :name>` / slot naming convention).
 *
 * JiT and S3 are alternative solutions, not fallbacks: when JiT is enabled, a failed
 * or empty JiT resolve returns null — never `getAudioURL`.
 */
export type { NarratableScreen };

/**
 * Inherit `placeholder-values` from parent when the child omits it (nested
 * `templates[]`, split panels).
 */
export function withInheritedPlaceholderValues<
  T extends Record<string, unknown>
>(child: T, parent?: Record<string, unknown> | null): T {
  return {
    ...child,
    'placeholder-values':
      (child['placeholder-values'] as
        | Record<string, string | number>
        | undefined) ??
      (parent?.['placeholder-values'] as
        | Record<string, string | number>
        | undefined)
  } as T;
}

// ---------------------------------------------------------------------------
// Main composable
// ---------------------------------------------------------------------------

/**
 * Screen object or `Ref` to one (e.g. `computed(() => props.screen)` in `uc-lightbox`).
 *
 * With live-recompiled course data (Pinia `compiled` uses `cloneDeep` + patches),
 * each edit can replace the screen object. Callers should pass `toRef(props,
 * 'screen')` or `computed(() => props.screen)` so `unref` reads the current prop,
 * not the plain object captured once at `setup`.
 */
export type ScreenAudioSource =
  | NarratableScreen
  | null
  | undefined
  | Ref<NarratableScreen | null | undefined>;

/** Static schema or `Ref` / `computed` — unwrapped on every slot resolution. */
export type ScreenAudioSchemaSource =
  | ScreenSchema
  | null
  | undefined
  | Ref<ScreenSchema | null | undefined>;

export default function useScreenAudio(
  /** Plain screen or `Ref` — unwrapped on every `resolveSlot` / `getSlotText` call. */
  screen: ScreenAudioSource,
  /** Plain schema or `Ref`/`computed` — unwrapped on every slot resolution (see `screen`). */
  schema: ScreenAudioSchemaSource
) {
  const { getAudioURL } = useAudio();
  const { resolveText, resolveNarrationAudio, audioAiJitEnabled } =
    useAudioNarration();

  const S = () => unref(screen) as NarratableScreen | null | undefined;
  const Z = () => unref(schema) as ScreenSchema | null | undefined;

  const textResolver = () => {
    const scr = S();
    const sch = Z();
    if (!scr || !sch) return null;
    return createScreenAudioTextResolver({
      screen: scr,
      schema: sch,
      resolveText: (key, value, values) =>
        resolveText(
          key,
          value,
          values ?? readPlaceholderValues(scr as Record<string, unknown>)
        )
    });
  };

  const getSlotText = (slotName: string): string =>
    textResolver()?.getSlotText(slotName) ?? '';

  const resolveAudioKey = (slotName: string): string =>
    textResolver()?.resolveAudioKey(slotName) ?? '';

  /**
   * Resolve the audio URL for a named slot (Tier 1 prop → Tier 3 JiT when
   * enabled → else Tier 2 S3; see file header and `docs/dev/jit-audio-schema.md`).
   * Returns null when audio is not available or not applicable.
   *
   * @param overrideText - Optional text to use for JiT synthesis instead of the
   *   schema-derived text. Useful when the narration content comes from a runtime
   *   source (e.g. a Pinia store) rather than screen-level YAML fields. Has no
   *   effect on S3 or prop-level audio — those paths only use the audio key/URL.
   */
  const resolveSlot = async (
    slotName: string,
    overrideText?: string,
    overrideKey?: string
  ): Promise<string | null> => {
    const scr = S();
    const sch = Z();
    if (!sch || !scr?.id) return null;
    if (scr['dont-read']) return null;

    // Tier 1 — prop-level audio on the intro slot (architecture doc: Tier 1)
    if (slotName === 'intro') {
      if (scr.audio) return scr.audio;
      if (scr.speech) return scr.speech;
    }

    const key = overrideKey ?? resolveAudioKey(slotName);
    const fill = readPlaceholderValues(
      scr as unknown as Record<string, unknown>
    );

    if (audioAiJitEnabled.value) {
      // Tier 3 — JiT only (never falls through to Tier 2 S3)
      const text =
        overrideText !== undefined && overrideText !== null
          ? resolveText(
              overrideKey ?? null,
              typeof overrideText === 'number'
                ? overrideText
                : String(overrideText),
              fill
            )
          : slotName === 'intro'
            ? (textResolver()?.getIntroJitText() ?? getSlotText(slotName))
            : getSlotText(slotName);

      if (!text) return null;
      return resolveNarrationAudio({ screenId: scr.id, key, text });
    }

    // Tier 2 — S3 pre-recorded course audio when JiT is disabled.
    // overrideKey is a JiT-only concern (distinct synthesized clips / translation
    // key, e.g. title vs subtitle); pre-recorded files follow the schema marker
    // convention, so resolve the S3 key from the slot, never the override.
    return getAudioURL(resolveAudioKey(slotName)) ?? null;
  };

  /**
   * Resolve and immediately play the audio for a named slot.
   * Uses the screen's id as the audio owner so it can be stopped/replaced cleanly.
   *
   * @param overrideText - Optional text for JiT synthesis (see `resolveSlot`).
   */
  const playSlot = async (
    slotName: string,
    overrideText?: string,
    overrideKey?: string
  ): Promise<void> => {
    const url = await resolveSlot(slotName, overrideText, overrideKey);
    const scr = S();
    // Await play() so that activeId is guaranteed to be set before playSlot
    // resolves. This lets callers use nowPlaying() / whenCurrentEnds()
    // immediately after `await playSlot(...)` without a race condition.
    if (url && scr?.id) await play(url, { owner: scr.id });
  };

  /**
   * Resolve and add the audio for a named slot to the play queue.
   * Intended for follow-on audio after a `playSlot()` call — because `play()`
   * clears the queue, `queueSlot()` must be called *after* `await playSlot()`
   * to avoid its result being discarded.
   *
   * Example (tabs: play intro, then queue initial tab):
   *   await playSlot('intro');
   *   queueSlot('tab-0'); // safe to fire-and-forget after the await
   *
   * @param overrideText - Optional text for JiT synthesis (see `resolveSlot`).
   */
  const queueSlot = async (
    slotName: string,
    overrideText?: string,
    overrideKey?: string
  ): Promise<void> => {
    const url = await resolveSlot(slotName, overrideText, overrideKey);
    const scr = S();
    if (url && scr?.id) queue(url, { owner: scr.id });
  };

  return { getSlotText, resolveSlot, playSlot, queueSlot };
}

// ---------------------------------------------------------------------------
// useChildScreenAudio — for container templates (sticky, slides)
// ---------------------------------------------------------------------------

/**
 * Composable for container templates that orchestrate audio across multiple
 * child screens (e.g. screen-sticky, screen-slides).
 *
 * Must be called in component setup. Returns:
 *   - `resolveChildIntro(screen)` — async function that resolves the intro
 *     audio URL for any child screen, using the same precedence as
 *     `resolveSlot`: Tier 1 prop audio → Tier 3 JiT when enabled (else null) →
 *     Tier 2 S3 via `getAudioURL` only when JiT is disabled.
 *   - `audioAiJitEnabled` — reactive flag so callers use JiT resolvers vs S3
 *     DOM attributes / `getAudioURL` explicitly (never both for the same clip).
 *
 * Usage:
 *   const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();
 *   // In a ScrollTrigger callback:
 *   const url = await resolveChildIntro(someChildScreen);
 *   if (url) play(url);
 */
export function useChildScreenAudio() {
  const { getAudioURL } = useAudio();
  const { resolveText, resolveNarrationAudio, audioAiJitEnabled } =
    useAudioNarration();

  const resolveChildIntro = async (
    screen: NarratableScreen,
    options?: { audioKey?: string }
  ): Promise<string | null> => {
    if (!screen?.id && !options?.audioKey) return null;
    if (screen['dont-read']) return null;

    // Tier 1 — prop-level audio (architecture doc: Tier 1)
    if (screen.audio) return screen.audio as string;
    if (screen.speech) return screen.speech as string;

    const schema = schemaForTemplate(screen.template as string | undefined);
    if (!schema) return null;

    const introSlot = schema.slots['intro'];
    if (!introSlot || isIteratedSlot(introSlot)) return null;

    const fill = readPlaceholderValues(
      screen as unknown as Record<string, unknown>
    );
    const resolveWithFill = (k: string, v: string | number) =>
      resolveText(k, v, fill);
    const api = createScreenAudioTextResolver({
      screen,
      schema,
      resolveText: resolveWithFill
    });
    const key = options?.audioKey ?? api.resolveAudioKey('intro');

    if (audioAiJitEnabled.value) {
      // Tier 3 — JiT only (never falls through to Tier 2 S3)
      const text = api.getIntroJitText();
      if (!text || !screen.id) return null;
      return resolveNarrationAudio({ screenId: screen.id, key, text });
    }

    // Tier 2 — S3 when JiT is disabled
    return getAudioURL(key) ?? null;
  };

  return { resolveChildIntro, audioAiJitEnabled };
}

// ---------------------------------------------------------------------------
// Split template + templates[] chain — shared URL resolution
// ---------------------------------------------------------------------------

/**
 * Stable id for `screen.templates[i]` when the YAML omits `id`.
 * Matches the `${screen.id}-${slotName}` dash convention (slot name `templates-0`).
 */
export function synthTemplatesChildId(parentId: string, index: number): string {
  return `${parentId}-templates-${index}`;
}

/**
 * Templates whose intros are not sequenced in `playIntroWithTemplatesChain` or
 * as split-panel first-child intros in `resolveSplitTemplateAudioUrls` — they
 * use overlay / on-demand audio instead (`uc-lightbox`, hotspot fullscreen, etc.).
 */
export const TEMPLATE_SKIP_INTRO_CHAIN = new Set<string>([
  'lightbox',
  'lightbox-video',
  'hotspot-fullscreen'
]);

/** Fallback host id for any screen object missing `id`, derived from editPath. */
function slugHostIdFromEditPath(editPath: string): string {
  const safe = editPath.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `tmpl-${safe || 'screen'}`;
}

/** Host id for split / split-* intro resolution (parent split row may lack `id`). */
export function splitTemplateHostId(
  splitScreen: NarratableScreen,
  editPath: string
): string {
  return splitScreen.id ?? slugHostIdFromEditPath(editPath);
}

function withSplitPanelChildId(
  hostId: string,
  childScreen: NarratableScreen,
  side: string,
  index: number
): NarratableScreen {
  return childScreen.id
    ? childScreen
    : { ...childScreen, id: `${hostId}_${side}_${index}` };
}

/**
 * Shared split-row resolution for runtime URLs and `audioDoc` marker names:
 * first left/right panel (with `left` vs `left-edge` precedence matching the template),
 * placeholder inheritance, skip list, and synthesised `${hostId}_${side}_0` ids.
 */
export function splitTemplateResolvedIntroTargets(
  splitScreen: NarratableScreen,
  editPath: string
): {
  hostId: string;
  host: NarratableScreen;
  leftForIntro: NarratableScreen | null;
  rightForIntro: NarratableScreen | null;
} {
  const hostId = splitTemplateHostId(splitScreen, editPath);
  const host = splitScreen.id ? splitScreen : { ...splitScreen, id: hostId };

  const leftScreens = (splitScreen.left ?? splitScreen['left-edge']) as
    | NarratableScreen[]
    | undefined;
  const rightScreens = (splitScreen.right ?? splitScreen['right-edge']) as
    | NarratableScreen[]
    | undefined;

  const left0 = leftScreens?.[0];
  const right0 = rightScreens?.[0];
  const splitAsRecord = splitScreen as unknown as Record<string, unknown>;

  const leftMerged = left0
    ? (withInheritedPlaceholderValues(
        left0 as unknown as Record<string, unknown>,
        splitAsRecord
      ) as NarratableScreen)
    : null;
  const rightMerged = right0
    ? (withInheritedPlaceholderValues(
        right0 as unknown as Record<string, unknown>,
        splitAsRecord
      ) as NarratableScreen)
    : null;

  const leftForIntro =
    leftMerged &&
    !TEMPLATE_SKIP_INTRO_CHAIN.has(String(leftMerged.template ?? ''))
      ? withSplitPanelChildId(hostId, leftMerged, 'left', 0)
      : null;
  const rightForIntro =
    rightMerged &&
    !TEMPLATE_SKIP_INTRO_CHAIN.has(String(rightMerged.template ?? ''))
      ? withSplitPanelChildId(hostId, rightMerged, 'right', 0)
      : null;

  return { hostId, host, leftForIntro, rightForIntro };
}

/**
 * Audio-marker / S3 key names for split / split-2-1 / split-1-2 (AuthorAudio script export).
 * Matches `resolveSplitTemplateAudioUrls` Tier 2 keys and JiT `resolveChildIntro` ids.
 */
export function splitTemplateAudioMarkerNames(
  splitScreen: NarratableScreen,
  editPath: string
): { parent: string; left0: string | null; right0: string | null } {
  const { hostId, leftForIntro, rightForIntro } =
    splitTemplateResolvedIntroTargets(splitScreen, editPath);
  return {
    parent: hostId,
    left0: leftForIntro?.id ?? null,
    right0: rightForIntro?.id ?? null
  };
}

/**
 * Audio URLs for a single split panel's first child, in play order.
 *
 * When the panel child carries its own `templates[]` chain (deep nesting — e.g.
 * a `cards` screen wrapped in a split side), resolve the whole chain; otherwise
 * resolve its single intro. Returns a list so the split sequence can flatten it.
 */
async function resolveSplitPanelAudioUrls(
  panel: NarratableScreen | null,
  editPath: string
): Promise<(string | null)[]> {
  if (!panel) return [];

  const templates = panel.templates as NarratableScreen[] | undefined;
  if (Array.isArray(templates) && templates.length > 0) {
    return resolveTemplatesChainAudioUrls(
      panel,
      panel.id ?? slugHostIdFromEditPath(editPath),
      ti => `${editPath}.templates.${ti}`
    );
  }

  const { resolveChildIntro } = useChildScreenAudio();
  return [await resolveChildIntro(panel)];
}

/**
 * Resolve intro audio URLs for a split / split-2-1 / split-1-2 screen in play order:
 * `[parent intro, ...left-0 chain, ...right-0 chain]`.
 *
 * Each panel's first child contributes its single intro, or — when the child
 * wraps its own `templates[]` chain — that whole chain (deep nesting).
 *
 * Used by `screen-split.vue` and by `uc-screen` when a split appears inside
 * `screen.templates[]`.
 */
export async function resolveSplitTemplateAudioUrls(
  splitScreen: NarratableScreen,
  editPath: string
): Promise<(string | null)[]> {
  const { host, leftForIntro, rightForIntro } =
    splitTemplateResolvedIntroTargets(splitScreen, editPath);
  const { resolveSlot } = useScreenAudio(host, screenSplitSchema);

  const [introUrl, leftUrls, rightUrls] = await Promise.all([
    resolveSlot('intro'),
    resolveSplitPanelAudioUrls(leftForIntro, `${editPath}.left.0`),
    resolveSplitPanelAudioUrls(rightForIntro, `${editPath}.right.0`)
  ]);

  return [introUrl, ...leftUrls, ...rightUrls];
}

/**
 * Resolve ordered intro audio URLs for a host screen that may declare `templates[]`
 * (same sequencing as `uc-screen` `playIntroWithTemplatesChain`).
 *
 * Used by `uc-screen` and by `screen-carousel` when a slide row wraps nested
 * screens under `templates`.
 *
 * @param splitEditPathForIndex - `editPath` string for each `resolveSplitTemplateAudioUrls` call at `templates[i]`.
 */
export async function resolveTemplatesChainAudioUrls(
  host: NarratableScreen,
  hostId: string,
  splitEditPathForIndex: (templateIndex: number) => string
): Promise<(string | null)[]> {
  const withId = (host.id ? host : { ...host, id: hostId }) as NarratableScreen;
  const schema = schemaForTemplate(withId.template as string | undefined);
  const { resolveSlot } = useScreenAudio(withId, schema);
  const { resolveChildIntro } = useChildScreenAudio();

  const urls: (string | null)[] = [];
  urls.push(await resolveSlot('intro'));

  const templates = withId.templates as NarratableScreen[] | undefined;
  if (!Array.isArray(templates) || !templates.length) {
    return urls;
  }

  const parentRecord = withId as unknown as Record<string, unknown>;

  for (let i = 0; i < templates.length; i++) {
    const raw = templates[i];
    const childWithId = (
      raw.id ? raw : { ...raw, id: synthTemplatesChildId(withId.id!, i) }
    ) as NarratableScreen;
    const child = withInheritedPlaceholderValues(
      childWithId as unknown as Record<string, unknown>,
      parentRecord
    ) as NarratableScreen;
    const t = child.template;
    if (TEMPLATE_SKIP_INTRO_CHAIN.has(String(t ?? ''))) continue;
    if (t === 'split' || t === 'split-2-1' || t === 'split-1-2') {
      const chunk = await resolveSplitTemplateAudioUrls(
        child,
        splitEditPathForIndex(i)
      );
      urls.push(...chunk);
    } else {
      urls.push(await resolveChildIntro(child));
    }
  }

  return urls;
}

/**
 * Resolve the ordered audio URLs for one slide of a carousel / slides container,
 * in play order. A slide can take one of three nesting shapes, each needing its
 * dedicated resolver (the same ones section-level sequencing uses):
 *   - a `templates[]` chain → `resolveTemplatesChainAudioUrls`
 *   - a `split` / `split-2-1` / `split-1-2` → `resolveSplitTemplateAudioUrls`
 *     (narration lives in the nested left/right panels, not the split's intro)
 *   - any other single nested `template` → that child's intro
 *
 * The container supplies the bits that genuinely differ between carousel and
 * slides: the stable slide id, the edit-path base, and — for a slide that
 * declares no nested template at all — an optional fallback (carousel's plain
 * title/text slide reads the container's own `slide-N` slot; slides has none).
 */
export async function resolveSlideAudioUrls(
  slide: NarratableScreen,
  parent: NarratableScreen,
  options: {
    slideId: string;
    editPathBase: string;
    /** Fallback when the slide has no `template`/`templates[]` (plain slide). */
    resolvePlainSlide?: () => Promise<string | null>;
  }
): Promise<string[]> {
  const slideWithId = { ...slide, id: options.slideId } as NarratableScreen;

  const templates = slide.templates as NarratableScreen[] | undefined;
  if (Array.isArray(templates) && templates.length > 0) {
    const urls = await resolveTemplatesChainAudioUrls(
      slideWithId,
      options.slideId,
      ti => `${options.editPathBase}.templates.${ti}`
    );
    return urls.filter((u): u is string => Boolean(u));
  }

  if (!slide.template && options.resolvePlainSlide) {
    const url = await options.resolvePlainSlide();
    return url ? [url] : [];
  }

  const merged = withInheritedPlaceholderValues(
    slideWithId as unknown as Record<string, unknown>,
    parent as unknown as Record<string, unknown>
  ) as NarratableScreen;

  const t = merged.template;
  if (t === 'split' || t === 'split-2-1' || t === 'split-1-2') {
    const urls = await resolveSplitTemplateAudioUrls(
      merged,
      options.editPathBase
    );
    return urls.filter((u): u is string => Boolean(u));
  }

  const { resolveChildIntro } = useChildScreenAudio();
  const url = await resolveChildIntro(merged);
  return url ? [url] : [];
}
